import React, { useEffect, useState } from 'react';
import { ERouteLinks } from 'models/route';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useNavigate, useParams } from 'react-router-dom';
import { StyledPageTitle } from 'theme/styles';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { getAccessoriesComment } from 'requests/ancillaries';
import { useQuery } from 'hooks/useQuery';
import { StyledDetailsWrapper } from 'pages/LegalEntitiesPage/LegalEntityDeleteCommentPage/styles';
import { ICommentData } from 'models/comment';
import { useTranslations } from 'hooks/useTranslations';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAncillaries } from '../../../hooks';

export interface IAccessoriesCommentData extends ICommentData {}

const AccessoriesDeleteCommentPage = () => {
  const { id, commentId } = useParams<{ id: string; commentId: string }>();
  const query = useQuery();
  const navigate = useNavigate();
  const [accessoriesComment, setAccessoriesComment] =
    useState<IAccessoriesCommentData | null>();
  const { t } = useTranslations();

  const { handleDeleteComment } = useAncillaries();

  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);

  const type: string = query.get('type')?.trim() || '';
  const { longDateFormat, getProperties } = useGlobalProperty();

  const navigateToDetails = () =>
    navigate(
      `${ERouteLinks.ViewAccessories.replace(
        ':id',
        id as string,
      )}?type=${type}&expand=comments`,
    );

  useEffect(() => {
    getProperties();
  }, []);

  useEffect(() => {
    (async function getAccessoriesCommentDataInit() {
      if (id && commentId) {
        const response = await getAccessoriesComment(id, commentId);
        if (response) {
          setAccessoriesComment(response);
        }
      }
    })();
  }, [id, commentId]);

  const onDelete = async () => {
    setIsDeleteLoading(true);
    try {
      if (accessoriesComment) {
        const result = await handleDeleteComment(
          accessoriesComment.uniqueId,
          accessoriesComment.hidden,
        );

        if (result.status === 204) {
          navigateToDetails();
        }
      }
    } catch (e: any) {
      console.error(e);
    }
    setIsDeleteLoading(false);
  };

  let accessoriesActionType: string = '';
  if (accessoriesComment) {
    accessoriesActionType = accessoriesComment.hidden
      ? t('enableComment.header')
      : t('disableComment.header');
  }

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('module.ancillaries.name'),
            route: ERouteLinks.Ancillaries,
          },
          {
            label: t('module.accessories.name'),
            route: `${ERouteLinks.Ancillaries}?expand=accessories`,
          },
          {
            label: t('accessoryDetails.accordion.title.label'),
            route: `${ERouteLinks.ViewAccessories.replace(
              ':id',
              id as string,
            )}?type=${type}&expand=comments`,
          },
          `${accessoriesActionType ? `${accessoriesActionType}` : ''}`,
        ]}
      />
      {accessoriesComment && (
        <>
          <StyledPageTitle variant="h4">{`${accessoriesActionType}`} </StyledPageTitle>

          <Grid size={{ xs: 12 }} style={{ margin: '10px 0 10px' }}>
            <Alert
              text={
                accessoriesComment.hidden
                  ? t('enableComment.info.text')
                  : t('disableComment.info.text')
              }
              variant={EAlertVariants.warning}
            />
          </Grid>

          <StyledDetailsWrapper container>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('enableComment.title.text')}
              </Typography>
              <Typography variant="body2">{accessoriesComment.title}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('enableComment.comment.text')}
              </Typography>
              <Typography variant="body2">{accessoriesComment.comment}</Typography>
            </Grid>
            <Grid size={{ xs: 12 }} container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }} container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('enableComment.addedBy.text')}
                  </Typography>
                  <Typography variant="body2">
                    {`${accessoriesComment.createdByFirstName} ${accessoriesComment.createdByLastName}`}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('enableComment.date.text')}
                  </Typography>
                  <Typography variant="body2">
                    {formatDate(accessoriesComment.createdDate, longDateFormat)}
                  </Typography>
                </Grid>
              </Grid>
              {accessoriesComment.hidden && (
                <Grid size={{ xs: 12, sm: 6 }} container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('enableComment.disabledBy.text')}
                    </Typography>
                    <Typography variant="body2">{`${accessoriesComment.hiddenByFirstName} ${accessoriesComment.hiddenByLastName}`}</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('enableComment.date.text')}
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(accessoriesComment.hiddenAt, longDateFormat)}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </StyledDetailsWrapper>
          <Grid container sx={{ mt: 2 }} spacing={2}>
            <Grid size={{ xs: 6, lg: 3 }}>
              <Button
                fullWidth
                label={t('general.yes.button')}
                id="yes-button"
                variant={EButtonVariants.contained}
                sx={{ mr: 1 }}
                onClick={async () => onDelete()}
                isLoading={isDeleteLoading}
              />
            </Grid>

            <Grid size={{ xs: 6, lg: 3 }}>
              <Button
                fullWidth
                label={t('general.no.button')}
                id="no-button"
                variant={EButtonVariants.outlined}
                onClick={navigateToDetails}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export { AccessoriesDeleteCommentPage };
